import { create } from "zustand";
import { Document, Folder } from "~/api/model";
import { getSelectableDocuments } from "../helpers";

interface DocumentSelectionState {
  selectedDocumentIds: string[];
  toggleDocumentSelection: (documentId: string) => void;
  toggleGroupSelection: (
    documents: Document[],
    allFilesNotProcessed: boolean,
    caseData?: Folder
  ) => void;
  toggleAllDocuments: (
    allDocuments: Document[],
    allFilesNotProcessed: boolean,
    caseData?: Folder
  ) => void;
  clearSelection: () => void;
  isDocumentSelected: (documentId: string) => boolean;
  isGroupFullySelected: (
    groupDocuments: Document[],
    allFilesNotProcessed: boolean,
    caseData?: Folder
  ) => boolean;
  isGroupPartiallySelected: (
    groupDocuments: Document[],
    allFilesNotProcessed: boolean,
    caseData?: Folder
  ) => boolean;
  getSelectedCount: () => number;
  isSelectionMode: () => boolean;
}

export const useDocumentSelectionStore = create<DocumentSelectionState>(
  (set, get) => ({
    selectedDocumentIds: [],

    toggleDocumentSelection: (documentId: string) => {
      set((state) => {
        if (state.selectedDocumentIds.includes(documentId)) {
          return {
            selectedDocumentIds: state.selectedDocumentIds.filter(
              (id) => id !== documentId
            ),
          };
        } else {
          return {
            selectedDocumentIds: [...state.selectedDocumentIds, documentId],
          };
        }
      });
    },

    toggleGroupSelection: (
      documents: Document[],
      allFilesNotProcessed: boolean,
      caseData?: Folder
    ) => {
      const selectableDocuments = getSelectableDocuments(
        documents,
        allFilesNotProcessed,
        caseData
      );
      const selectableDocumentIds = selectableDocuments.map((doc) => doc.id);

      const allSelected =
        selectableDocuments.length > 0 &&
        selectableDocuments.every((doc) =>
          get().selectedDocumentIds.includes(doc.id)
        );

      if (allSelected) {
        set((state) => ({
          selectedDocumentIds: state.selectedDocumentIds.filter(
            (id) => !selectableDocumentIds.includes(id)
          ),
        }));
      } else {
        set((state) => {
          const newSelection = [...state.selectedDocumentIds];
          selectableDocumentIds.forEach((id) => {
            if (!newSelection.includes(id)) {
              newSelection.push(id);
            }
          });
          return { selectedDocumentIds: newSelection };
        });
      }
    },

    toggleAllDocuments: (
      allDocuments: Document[],
      allFilesNotProcessed: boolean,
      caseData?: Folder
    ) => {
      const selectableDocuments = getSelectableDocuments(
        allDocuments,
        allFilesNotProcessed,
        caseData
      );
      const selectableDocumentIds = selectableDocuments.map((doc) => doc.id);

      const allSelected =
        selectableDocumentIds.length > 0 &&
        selectableDocumentIds.every((id) =>
          get().selectedDocumentIds.includes(id)
        );

      if (allSelected) {
        set({ selectedDocumentIds: [] });
      } else {
        set({ selectedDocumentIds: selectableDocumentIds });
      }
    },

    clearSelection: () => {
      set({ selectedDocumentIds: [] });
    },

    isDocumentSelected: (documentId: string) => {
      return get().selectedDocumentIds.includes(documentId);
    },

    isGroupFullySelected: (
      groupDocuments: Document[],
      allFilesNotProcessed: boolean,
      caseData?: Folder
    ) => {
      const selectableDocuments = getSelectableDocuments(
        groupDocuments,
        allFilesNotProcessed,
        caseData
      );
      const groupIds = selectableDocuments.map((doc) => doc.id);
      return (
        groupIds.length > 0 &&
        groupIds.every((id) => get().selectedDocumentIds.includes(id))
      );
    },

    isGroupPartiallySelected: (
      groupDocuments: Document[],
      allFilesNotProcessed: boolean,
      caseData?: Folder
    ) => {
      const selectableDocuments = getSelectableDocuments(
        groupDocuments,
        allFilesNotProcessed,
        caseData
      );
      const groupIds = selectableDocuments.map((doc) => doc.id);
      const anySelected = groupIds.some((id) =>
        get().selectedDocumentIds.includes(id)
      );
      const allSelected = get().isGroupFullySelected(
        groupDocuments,
        allFilesNotProcessed,
        caseData
      );
      return anySelected && !allSelected;
    },

    getSelectedCount: () => {
      return get().selectedDocumentIds.length;
    },

    isSelectionMode: () => {
      return get().selectedDocumentIds.length > 0;
    },
  })
);
